import { Container, Graphics, Text } from 'pixi.js';
import type { ViewportLayout } from '../app/Viewport';
import { UI_COLORS, UI_FONT_FAMILY } from './Theme';

export interface DebugOverlaySnapshot {
  sceneName: string;
  currentScenarioId: string;
  currentCaseId: string;
  nodeId: string;
  lineId: string | null;
  lineIndex: number;
  currentBgmId: string | null;
  currentBgmPlaceholder: boolean;
  backgroundId: string | null;
  backgroundEffect: string;
  backgroundPlaceholder: boolean;
  inputLocked: boolean;
  inputLockReasons: Record<string, number>;
  novelBlocked: boolean;
  novelBlockReasons: string[];
}

export interface DebugOverlayState {
  enabled: boolean;
  snapshot: DebugOverlaySnapshot | null;
}

const PANEL_WIDTH = 470;
const PANEL_PADDING = 12;
const PANEL_RADIUS = 4;
const LINE_HEIGHT = 18;

export class DebugOverlay extends Container {
  private readonly background = new Graphics();
  private readonly text = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 14,
      fontWeight: '700',
      lineHeight: LINE_HEIGHT
    }
  });

  private enabled = false;
  private snapshot: DebugOverlaySnapshot | null = null;

  public constructor() {
    super();
    this.eventMode = 'none';
    this.visible = false;
    this.alpha = 0.92;
    this.addChild(this.background);
    this.addChild(this.text);
    this.text.position.set(PANEL_PADDING, PANEL_PADDING);
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.visible = enabled;
    this.render();
  }

  public setSnapshot(snapshot: DebugOverlaySnapshot): void {
    this.snapshot = snapshot;
    this.render();
  }

  public resize(layout: ViewportLayout): void {
    this.position.set(layout.safeArea.x + 12, layout.safeArea.y + 12);
    this.render();
  }

  public getState(): DebugOverlayState {
    return {
      enabled: this.enabled,
      snapshot: this.snapshot
        ? {
            ...this.snapshot,
            inputLockReasons: { ...this.snapshot.inputLockReasons },
            novelBlockReasons: [...this.snapshot.novelBlockReasons]
          }
        : null
    };
  }

  private render(): void {
    this.background.clear();
    if (!this.enabled || !this.snapshot) {
      this.text.text = '';
      return;
    }

    this.text.text = formatSnapshot(this.snapshot);
    const panelHeight = this.text.height + PANEL_PADDING * 2;
    this.background
      .roundRect(0, 0, PANEL_WIDTH, panelHeight, PANEL_RADIUS)
      .fill({ color: UI_COLORS.black, alpha: 0.82 })
      .stroke({ color: UI_COLORS.gold, alpha: 0.68, width: 1 });
  }
}

function formatSnapshot(snapshot: DebugOverlaySnapshot): string {
  return [
    `scene: ${snapshot.sceneName}`,
    `case: ${snapshot.currentCaseId} / scenario: ${snapshot.currentScenarioId}`,
    `node: ${snapshot.nodeId} / line: ${snapshot.lineId ?? '-'} #${snapshot.lineIndex}`,
    `bgm: ${snapshot.currentBgmId ?? '-'}${snapshot.currentBgmPlaceholder ? ' (placeholder)' : ''}`,
    `bg: ${snapshot.backgroundId ?? '-'} / ${snapshot.backgroundEffect}${
      snapshot.backgroundPlaceholder ? ' (placeholder)' : ''
    }`,
    `input: ${snapshot.inputLocked ? 'LOCKED' : 'ok'} ${formatRecord(snapshot.inputLockReasons)}`,
    `novel: ${snapshot.novelBlocked ? 'BLOCKED' : 'ok'} ${snapshot.novelBlockReasons.join(', ')}`
  ].join('\n');
}

function formatRecord(record: Record<string, number>): string {
  const entries = Object.entries(record);
  if (entries.length === 0) {
    return '-';
  }

  return entries.map(([key, value]) => `${key}:${value}`).join(', ');
}
